import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/** API origin without /api suffix (Google OAuth redirect). */
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const USER_CACHE_KEY = 'evplanner.user';

let csrfToken = null;
let csrfBootstrapPromise = null;

/**
 * Wrapper consistente para respostas da API.
 */
export class APIResponse {
  constructor(success, data, error = null) {
    this.success = success;
    this.data = data;
    this.error = error;
  }

  static success(data) {
    return new APIResponse(true, data);
  }

  static error(error) {
    return new APIResponse(false, null, error);
  }
}

function readCachedUser() {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_err) {
    return null;
  }
}

function writeCachedUser(user) {
  if (user) {
    sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(USER_CACHE_KEY);
  }
}

/** Optional UI cache for current user (no JWT tokens). */
export const authStorage = {
  read: () => {
    const user = readCachedUser();
    return user ? { user } : null;
  },
  write: ({ user }) => writeCachedUser(user ?? null),
  clear: () => writeCachedUser(null),
  key: USER_CACHE_KEY,
};

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function readCsrfFromCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/** Prefer cookie (source of truth) over in-memory cache. */
export function syncCsrfFromCookie() {
  const cookieToken = readCsrfFromCookie();
  if (cookieToken) {
    csrfToken = cookieToken;
  }
  return csrfToken;
}

export async function ensureCsrfCookie(force = false) {
  if (!force) {
    syncCsrfFromCookie();
    if (csrfToken) return csrfToken;
  }

  if (!csrfBootstrapPromise || force) {
    if (force) {
      csrfBootstrapPromise = null;
    }
    csrfBootstrapPromise = axios
      .get(`${API_BASE_URL}/auth/csrf/`, { withCredentials: true })
      .then((res) => {
        csrfToken = readCsrfFromCookie() || res.data?.csrfToken || null;
        return csrfToken;
      })
      .catch(() => {
        csrfBootstrapPromise = null;
        return null;
      });
  }
  return csrfBootstrapPromise;
}

export function clearCsrfToken() {
  csrfToken = null;
  csrfBootstrapPromise = null;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

apiClient.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    await ensureCsrfCookie();
    syncCsrfFromCookie();
    const token = csrfToken || readCsrfFromCookie();
    if (token) {
      config.headers['X-CSRFToken'] = token;
    }
  }
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

function resolveQueue(error) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  refreshQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status === 403 &&
      original &&
      !original._csrfRetry &&
      MUTATING_METHODS.has((original.method || 'get').toLowerCase())
    ) {
      original._csrfRetry = true;
      clearCsrfToken();
      await ensureCsrfCookie(true);
      syncCsrfFromCookie();
      return apiClient(original);
    }

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/token/') &&
      !original.url?.includes('/auth/login/') &&
      !original.url?.includes('/auth/register/') &&
      !original.url?.includes('/auth/csrf/')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => apiClient(original));
      }

      original._retry = true;
      isRefreshing = true;
      try {
        clearCsrfToken();
        await ensureCsrfCookie(true);
        syncCsrfFromCookie();
        const refreshHeaders = csrfToken ? { 'X-CSRFToken': csrfToken } : {};
        await axios.post(
          `${API_BASE_URL}/auth/token/refresh/`,
          {},
          {
            withCredentials: true,
            headers: refreshHeaders,
          },
        );
        await ensureCsrfCookie(true);
        syncCsrfFromCookie();
        resolveQueue(null);
        return apiClient(original);
      } catch (refreshError) {
        resolveQueue(refreshError);
        authStorage.clear();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export { API_BASE_URL };

/**
 * Devolve `response.data` em caso de sucesso, ou atira `response.error`.
 * Usado por todos os hooks TanStack Query para desempacotar a APIResponse.
 */
export function unwrap(response, fallback = null) {
  if (!response) return fallback;
  if (response.success) return response.data;
  throw response.error || new Error('API error');
}

export function extractApiError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    if (data.trim().toLowerCase().startsWith('<!doctype html')) {
      return fallback;
    }
    return data;
  }
  if (data && typeof data === 'object') {
    return data;
  }
  return error?.message || fallback;
}

/**
 * Serializa `params` numa query string, omitindo chaves vazias/nulas.
 * Usado por todos os wrappers data/* para construir URLs paginados.
 */
export function buildQuery(params) {
  const cleaned = Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
  const qs = new URLSearchParams(cleaned).toString();
  return qs ? `?${qs}` : '';
}

export function toFormData(fields) {
  const body = new FormData();  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (value instanceof Blob || (value && typeof value === 'object' && 'size' in value && 'name' in value)) {
      body.append(key, value);
      return;
    }
    body.append(key, typeof value === 'string' ? value : String(value));
  });
  return body;
}

export function apiPathFromAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const base = API_BASE_URL.replace(/\/$/, '');
  // Fast path: only when `base` is a proper origin/path boundary (next char
  // is `/` or url === base). Avoids matching subdomain prefixes such as
  // `https://api.evplanner.eu.evil.com/...` — origin invariant.
  if (url === base) return '/';
  if (url.startsWith(`${base}/`)) {
    return url.slice(base.length); // inclui o `/` inicial
  }
  try {
    const parsed = new URL(url);
    const baseParsed = new URL(API_BASE_URL);
    if (
      parsed.origin === baseParsed.origin &&
      parsed.pathname.startsWith(baseParsed.pathname)
    ) {
      const suffix = parsed.pathname.slice(baseParsed.pathname.length) + parsed.search;
      if (!suffix || suffix === '') return '/';
      return suffix.startsWith('/') ? suffix : `/${suffix}`;
    }
    return `${parsed.pathname}${parsed.search}`;
  } catch (_err) {
    return url;
  }
}
